import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RcSelectComponent } from '../../../shared/components/rc-select/rc-select.component';
import { ACTIVITY_TYPES, ActivityType } from '../../models/activity-type';

@Component({
    selector: 'app-activity-picker',
    imports: [FormsModule, RcSelectComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    template: `
        <div class="space-y-1">
            <div class="grid grid-cols-2 gap-1">
                <button type="button" (click)="pick('sur-site')" [disabled]="disabled()"
                    [class]="'flex items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-[11px] font-medium transition-colors focus-visible:ring-2 focus-visible:ring-main_color ' +
                        (value() === 'sur-site'
                            ? 'border-main_color bg-main_color/15 text-main_color dark:bg-main_color/20'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-main_color/50 hover:text-main_color dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-main_color/50 dark:hover:text-main_color_2' + (isOther() ? ' opacity-40' : '')) +
                        (disabled() ? ' cursor-not-allowed opacity-60' : '')">
                    <span aria-hidden="true">🏢</span><span class="truncate">Sur site</span>
                </button>
                <button type="button" (click)="pick('teletravail')" [disabled]="disabled()"
                    [class]="'flex items-center justify-center gap-1 rounded-md border px-1 py-1.5 text-[11px] font-medium transition-colors focus-visible:ring-2 focus-visible:ring-main_color ' +
                        (value() === 'teletravail'
                            ? 'border-main_color_2 bg-main_color_2/15 text-main_color_2 dark:bg-main_color_2/20'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-main_color_2/50 hover:text-main_color_2 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-main_color_2/50' + (isOther() ? ' opacity-40' : '')) +
                        (disabled() ? ' cursor-not-allowed opacity-60' : '')">
                    <span aria-hidden="true">🏠</span><span class="truncate">Télétravail</span>
                </button>
            </div>
            <!-- Anneau mis en avant quand "Autres" est la sélection active, pour la même raison
                 que les boutons ci-dessus sont grisés : sans ça les trois contrôles ont l'air
                 tout aussi "neutres" et rien n'indique lequel des trois porte la valeur. -->
            <div [class]="isOther() ? 'rounded-md ring-2 ring-main_color/50' : ''">
            @if (disabled()) {
                <div class="w-full truncate rounded-md border border-slate-200 bg-slate-100 px-1.5 py-1.5 text-[11px] font-medium text-slate-500 opacity-60 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                    {{ isOther() ? currentLabel() : 'Autres' }}
                </div>
            } @else {
                <app-rc-select [items]="options()" bindLabel="label" bindValue="value"
                    [ngModel]="isOther() ? value() : null" (ngModelChange)="onSelect($event)"
                    [searchable]="false" [clearable]="false"
                    placeholder="Autres"
                    customClass="compact-select w-full"
                    ariaLabel="Autre activité">
                </app-rc-select>
            }
            </div>
        </div>
    `
})
export class ActivityPickerComponent {
    value = input<string>('');
    disabled = input<boolean>(false);
    options = input<ActivityType[]>(ACTIVITY_TYPES);
    valueChange = output<string>();

    isOther = computed(() => {
        const v = this.value();
        return !!v && v !== 'sur-site' && v !== 'teletravail';
    });

    currentLabel = computed(() =>
        this.options().find(o => o.value === this.value())?.label ?? 'Autres'
    );

    pick(activity: 'sur-site' | 'teletravail'): void {
        if (this.disabled()) return;
        this.valueChange.emit(activity);
    }

    onSelect(value: string | null): void {
        // ng-select peut émettre null/undefined au simple resync (changement de semaine
        // qui passe d'une activité "Autres" à Sur site / Télétravail). Ne pas écraser
        // la valeur réelle du parent dans ce cas — les boutons gèrent ces deux activités.
        if (!value) return;
        this.valueChange.emit(value);
    }
}
